class CustomFoodMenu extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.items = [...this.querySelectorAll('[data-menu-item]')];
    this.search = this.querySelector('[data-menu-search]');
    this.empty = this.querySelector('[data-menu-empty]');
    this.pagination = this.querySelector('[data-menu-pagination]');
    this.pageNumbers = this.querySelector('[data-page-numbers]');
    this.previousPageButton = this.querySelector('[data-page-previous]');
    this.nextPageButton = this.querySelector('[data-page-next]');
    this.itemsPerPage = Math.max(1, Number(this.dataset.itemsPerPage) || 12);
    this.currentPage = 1;
    this.dialog = this.querySelector('[data-menu-dialog]');
    this.dialogContent = this.querySelector('[data-dialog-content]');
    this.activeCategory = 'all';
    this.lastTrigger = null;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.activateTab(tab));
      tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index));
    });

    this.search?.addEventListener('input', () => {
      this.currentPage = 1;
      this.filterItems();
    });
    this.previousPageButton?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    this.nextPageButton?.addEventListener('click', () => this.goToPage(this.currentPage + 1));
    this.pageNumbers?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-page]');
      if (button) this.goToPage(Number(button.dataset.page));
    });
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-menu-item-trigger]');
      if (trigger) this.openDialog(trigger);
      if (event.target.closest('[data-dialog-close]')) this.closeDialog();
    });

    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.closeDialog();
    });

    this.dialog?.addEventListener('close', () => {
      this.unlockPageScroll();
      this.lastTrigger?.focus();
      this.lastTrigger = null;
    });

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest('[data-menu-item]');
        if (!item) return;
        const tab = this.tabs.find((candidate) => candidate.dataset.category === item.dataset.category);
        if (tab) this.activateTab(tab);
      });
    }

    this.filterItems();
  }

  disconnectedCallback() {
    this.unlockPageScroll();
  }

  activateTab(activeTab, moveFocus = false) {
    this.activeCategory = activeTab.dataset.category || 'all';
    this.currentPage = 1;
    this.tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });
    this.filterItems();
    if (moveFocus) activeTab.focus();
  }

  handleTabKeydown(event, index) {
    let nextIndex;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex === undefined) return;
    event.preventDefault();
    this.activateTab(this.tabs[nextIndex], true);
  }

  filterItems() {
    const query = (this.search?.value || '').trim().toLocaleLowerCase();
    const matchingItems = this.items.filter((item) => {
      const categoryMatches = this.activeCategory === 'all' || item.dataset.category === this.activeCategory;
      const searchMatches = !query || (item.dataset.search || '').includes(query);
      return categoryMatches && searchMatches;
    });
    const shouldPaginate = this.activeCategory === 'all' && matchingItems.length > this.itemsPerPage;
    const totalPages = shouldPaginate ? Math.ceil(matchingItems.length / this.itemsPerPage) : 1;
    this.currentPage = Math.min(Math.max(this.currentPage, 1), totalPages);
    const pageStart = (this.currentPage - 1) * this.itemsPerPage;
    const pageItems = shouldPaginate
      ? matchingItems.slice(pageStart, pageStart + this.itemsPerPage)
      : matchingItems;

    this.items.forEach((item) => {
      item.hidden = !pageItems.includes(item);
    });
    if (this.empty) this.empty.hidden = matchingItems.length > 0;
    this.updatePagination(shouldPaginate, totalPages);
  }

  goToPage(page) {
    if (!Number.isFinite(page) || page === this.currentPage) return;
    this.currentPage = page;
    this.filterItems();
    this.querySelector('.custom-food-menu__toolbar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updatePagination(showPagination, totalPages) {
    if (!this.pagination || !this.pageNumbers) return;
    this.pagination.hidden = !showPagination;
    if (!showPagination) {
      this.pageNumbers.replaceChildren();
      return;
    }

    this.previousPageButton.disabled = this.currentPage === 1;
    this.nextPageButton.disabled = this.currentPage === totalPages;
    const fragment = document.createDocumentFragment();
    for (let page = 1; page <= totalPages; page += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.page = String(page);
      button.textContent = String(page);
      button.setAttribute('aria-label', `Menu page ${page}`);
      if (page === this.currentPage) button.setAttribute('aria-current', 'page');
      fragment.append(button);
    }
    this.pageNumbers.replaceChildren(fragment);
  }

  openDialog(trigger) {
    const item = trigger.closest('[data-menu-item]');
    const template = item?.querySelector('template');
    if (!template || !this.dialogContent || !this.dialog) return;
    this.lastTrigger = trigger;
    this.dialogContent.replaceChildren(template.content.cloneNode(true));
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    document.body.classList.add('custom-food-menu-modal-open');
  }

  closeDialog() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else {
      this.dialog.removeAttribute('open');
      this.unlockPageScroll();
    }
  }

  unlockPageScroll() {
    const openFoodDialog = document.querySelector('custom-food-menu [data-menu-dialog][open]');
    if (!openFoodDialog) document.body.classList.remove('custom-food-menu-modal-open');
  }
}

if (!customElements.get('custom-food-menu')) {
  customElements.define('custom-food-menu', CustomFoodMenu);
}
